import Papa from 'papaparse';

const validGameHalves       = ['1','2','3'];
const validMethods          = ['Left foot','Right foot','Hand'];
const validPressures        = ['Low','Medium','High'];
const validSetPlays         = [
  'Open Play',
  'Free Kick from Hands',
  'Free Kick from Ground',
  'Penalty',
  '45m Kick',
  'Mark'
];
const validOutcomes         = [
  'Point','2 Point','Wide','Post','Short','Blocked',
  'Goal','Saved','45'
];
const validShotTypes        = ['Goal attempt','Point attempt','2 Point attempt'];
const validNewAttackOptions = ['Yes','No'];
const validGameTypes        = ['Mens', 'Ladies'];

const requiredColumns = [
  "Team", "PlayerName", "GameHalf", "GameTimeSeconds", "ShotPressure",
  "ShotType", "SetPlay", "ShotMethod", "Distance", "Angle", "Side",
  "ShotOutcome", "NewAttack"
];

const numericFields = [
  { name: "Distance", min: -100, max: 200 },
  { name: "Angle", min: 0, max: 360 },
  { name: "GameTimeSeconds", min: 0, max: 10000 }
];

const enumFields = [
  { name: "GameHalf", valid: validGameHalves },
  { name: "ShotMethod", valid: validMethods },
  { name: "ShotPressure", valid: validPressures },
  { name: "SetPlay", valid: validSetPlays },
  { name: "ShotOutcome", valid: validOutcomes },
  { name: "ShotType", valid: validShotTypes },
  { name: "NewAttack", valid: validNewAttackOptions }
];

// Helper function to find a column by name (case-insensitive)
function findColumnKey(headerCols, targetName) {
  return headerCols.find(h => h.toLowerCase() === targetName.toLowerCase());
}

function getOptionalColumnValue(row, headerCols, targetName) {
  const key = findColumnKey(headerCols, targetName);
  if (!key) return null;
  const value = String(row[key] ?? "").trim().replace(/^"|"$/g, '');
  return value === "" ? null : value;
}

export function validateCsvBasic(csvText, homeTeam, awayTeam, gameType = "Mens") {
  const errors = [];
  
  if (!csvText || typeof csvText !== "string") {
    return { valid: false, errors: ["No CSV data provided."] };
  }

  const { data, errors: parseErrors } = Papa.parse(csvText, {
    header: true,
    skipEmptyLines: true,
    dynamicTyping: false,
  });

  if (parseErrors.length > 0) {
    errors.push(`CSV parsing error: ${parseErrors[0].message}`);
    return { valid: false, errors };
  }

  if (data.length === 0) {
    return { valid: false, errors: ["No data rows found."] };
  }

  const headerCols = Object.keys(data[0]);
  const hasGameTypeColumn = Boolean(findColumnKey(headerCols, "GameType"));

  // Check required columns
  for (const col of requiredColumns) {
    const found = findColumnKey(headerCols, col);
    if (!found) {
      errors.push(`Missing required column: "${col}"`);
    }
  }
  if (errors.length) return { valid: false, errors };

  // Validate each row
  data.forEach((row, i) => {
    const rowNum = i + 2;

    // Required fields not empty
    for (const col of requiredColumns) {
      const colKey = findColumnKey(headerCols, col);
      const val = row[colKey];
      if (!val || String(val).trim() === "") {
        errors.push(`Row ${rowNum}: "${col}" is empty.`);
      }
    }

    // Numeric fields validation
    for (const { name, min, max } of numericFields) {
      const colKey = findColumnKey(headerCols, name);
      const rawVal = String(row[colKey]).trim().replace(/^"|"$/g, ''); // Strip quotes
      const val = Number(rawVal);
      if (isNaN(val)) {
        errors.push(`Row ${rowNum}: "${name}" is not a number.`);
      } else if (val < min || val > max) {
        errors.push(`Row ${rowNum}: "${name}" (${val}) out of range [${min}, ${max}].`);
      }
    }

    // Enum fields validation
    for (const { name, valid } of enumFields) {
      const colKey = findColumnKey(headerCols, name);
      const val = String(row[colKey]).trim().replace(/^"|"$/g, ''); // Strip quotes
      if (!valid.includes(val)) {
        errors.push(`Row ${rowNum}: "${name}" = "${val}" is invalid.`);
      }
    }

    // Team check
    if (homeTeam && awayTeam) {
      const teamKey = findColumnKey(headerCols, "Team");
      const teamVal = String(row[teamKey]).trim();
      const homeT = String(homeTeam).trim();
      const awayT = String(awayTeam).trim();
      if (teamVal !== homeT && teamVal !== awayT) {
        errors.push(`Row ${rowNum}: "Team" = "${teamVal}" is not "${homeT}" or "${awayT}".`);
      }
    }

    if (hasGameTypeColumn) {
      const gameTypeKey = findColumnKey(headerCols, "GameType");
      const csvGameType = String(row[gameTypeKey]).trim().replace(/^"|"$/g, '');
      const normalizedGameType = gameType === "Ladies" ? "Ladies" : "Mens";
      if (csvGameType && !validGameTypes.includes(csvGameType)) {
        errors.push(`Row ${rowNum}: "GameType" = "${csvGameType}" is invalid.`);
      } else if (csvGameType && csvGameType !== normalizedGameType) {
        errors.push(
          `Row ${rowNum}: "GameType" = "${csvGameType}" does not match the selected game type.`,
        );
      }
    }

    const windStrength = getOptionalColumnValue(row, headerCols, "WindStrength");
    if (windStrength !== null) {
      const value = Number(windStrength);
      if (Number.isNaN(value) || value < 0) {
        errors.push(`Row ${rowNum}: "WindStrength" must be a non-negative number.`);
      }
    }

    const windAngle = getOptionalColumnValue(row, headerCols, "WindAngle");
    if (windAngle !== null) {
      const value = Number(windAngle);
      if (Number.isNaN(value) || value < 0 || value > 345 || value % 15 !== 0) {
        errors.push(`Row ${rowNum}: "WindAngle" must be between 0 and 345 in 15-degree steps.`);
      }
    }
  });

  return { valid: errors.length === 0, errors };
}
